import React, { useState, useEffect, useMemo, useRef } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getMenu,
  createCategory,
  updateCategory,
  deleteCategory,
  deleteItem,
  updateItem,
  toggleItemAvailability,
  createItem,
  createModifierGroup,
  getModifierGroups,
} from '../services/api';
import ImageUpload from '../components/ImageUpload';
import TemplatePicker from '../components/TemplatePicker';
import LazyImage from '../components/LazyImage';
import ActionMenu from '../components/ActionMenu';
import MenuNav from '../components/MenuNav';
import TranslatableInput from '../components/TranslatableInput';
import {
  Camera,
  ChevronUp,
  ChevronDown,
  Clock,
  Sparkles,
  Sandwich,
  Pizza,
  Coffee,
  Flame,
  Beer,
  UtensilsCrossed,
} from 'lucide-react';
import { THUMBNAIL_SIZES } from '../utils/thumbnailUtils';
import { formatCurrency } from '../utils/formatCurrency';
import { prepareItemForEdit } from '../utils/itemForm';
import { storage } from '../utils/storage';
import { STARTER_MENUS, countStarterItems } from '../data/starterMenus';
import { buildStarterPlan, applyStarterPlan, countPlanSteps } from '../utils/starterMenus';
import './MenuPage.css';

// Iconos de las cartas iniciales por rubro (NER-4488 fase 3).
const STARTER_ICONS = { Sandwich, Pizza, Coffee, Flame, Beer, UtensilsCrossed };

function StarterIcon({ name }) {
  const Icon = STARTER_ICONS[name] || Sparkles;
  return <Icon size={20} aria-hidden="true" />;
}

function MenuPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const newCategoryFormRef = useRef(null);

  // Carta inicial por rubro (NER-4488 fase 3)
  const [showStarter, setShowStarter] = useState(false);
  const [starterProgress, setStarterProgress] = useState(null);
  // Rubro pendiente de confirmar cuando la carta ya tiene categorías (antes era
  // un confirm() nativo que las herramientas de QA no ven).
  const [starterConfirmId, setStarterConfirmId] = useState(null);

  // Category Form State
  const [newCategory, setNewCategory] = useState({
    name: '',
    displayOrder: 1,
    imageUrl: '',
    translations: {},
    timeRange: null,
    preparationArea: 'kitchen',
  });

  // Edición de categoría
  const [editingCategory, setEditingCategory] = useState(null);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editCategoryForm, setEditCategoryForm] = useState({
    name: '',
    displayOrder: 1,
    imageUrl: '',
    translations: {},
    timeRange: null,
    preparationArea: 'kitchen',
  });

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Category form errors
  const [categoryErrors, setCategoryErrors] = useState({});

  // Toast feedback
  const [toast, setToast] = useState(null);
  const toastTimerRef = React.useRef(null);

  // Confirmaciones de eliminación
  const [showDeleteCategoryConfirm, setShowDeleteCategoryConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Escape cierra los modales (auditoría de popups QA 2026-09-13).
  useEscapeKey(showEditCategoryModal, () => setShowEditCategoryModal(false));
  useEscapeKey(showDeleteCategoryConfirm, () => setShowDeleteCategoryConfirm(false));
  useEscapeKey(showDeleteItemConfirm, () => setShowDeleteItemConfirm(false));
  useEscapeKey(Boolean(starterConfirmId), () => setStarterConfirmId(null));

  const filteredMenu = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return menu
      .filter((cat) => !filterCategory || cat.categoryId === filterCategory)
      .map((cat) => {
        const filteredItems = (cat.items || []).filter((item) => {
          if (
            query &&
            !item.name?.toLowerCase().includes(query) &&
            !item.description?.toLowerCase().includes(query)
          ) {
            return false;
          }
          if (filterStatus === 'available' && !item.isAvailable) return false;
          if (filterStatus === 'unavailable' && item.isAvailable) return false;
          return true;
        });
        return { ...cat, items: filteredItems };
      })
      .filter((cat) => (!query && !filterStatus ? true : cat.items.length > 0));
  }, [menu, searchQuery, filterCategory, filterStatus]);

  useEffect(() => {
    loadMenu();
  }, []);

  useEffect(() => {
    if (showNewCategoryForm && newCategoryFormRef.current) {
      newCategoryFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [showNewCategoryForm]);

  const loadMenu = async () => {
    try {
      const response = await getMenu();
      // Filtrar categorías e items eliminados
      const activeMenu = (response.menu || [])
        .filter((cat) => cat.status !== 'deleted')
        .map((cat) => ({
          ...cat,
          items: (cat.items || []).filter((item) => item.status !== 'deleted'),
        }));
      setMenu(activeMenu);
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.name.trim()) {
      setCategoryErrors({ name: t('menu.validation.nameRequired') });
      return;
    }
    setCategoryErrors({});
    try {
      await createCategory(newCategory);
      setNewCategory({
        name: '',
        displayOrder: 1,
        imageUrl: '',
        translations: {},
        timeRange: null,
        preparationArea: 'kitchen',
      });
      setShowNewCategoryForm(false);
      loadMenu();
    } catch (error) {
      alert(t('common.error'));
    }
  };

  const showToast = (message, undoCallback) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, undoCallback });
    toastTimerRef.current = setTimeout(() => setToast(null), 5000);
  };

  // Carta inicial por rubro: crea categorías + items (no disponibles) + grupos
  // reutilizando el CRUD existente, saltando lo que ya existe por nombre.
  const handleApplyStarter = async (starterId, { confirmed = false } = {}) => {
    if (starterProgress?.running) return;
    if (menu.length > 0 && !confirmed) {
      setStarterConfirmId(starterId);
      return;
    }
    setStarterConfirmId(null);
    const plan = buildStarterPlan(starterId, t);
    if (!plan) return;
    const restaurantId = storage.get('selectedRestaurantId');
    setStarterProgress({ running: true, done: 0, total: countPlanSteps(plan), label: '' });
    try {
      let existingGroups = [];
      try {
        const groupsData = await getModifierGroups(restaurantId);
        existingGroups = groupsData.modifierGroups || [];
      } catch (error) {
        console.error('Error loading modifier groups for starter menu:', error);
      }
      const summary = await applyStarterPlan(plan, {
        apis: { createCategory, createItem, toggleItemAvailability, createModifierGroup },
        existing: { categories: menu, modifierGroups: existingGroups },
        restaurantId,
        onProgress: (done, total, label) =>
          setStarterProgress({ running: true, done, total, label }),
      });
      await loadMenu();
      setShowStarter(false);
      showToast(
        summary.stoppedAt
          ? t('starterMenus.stopped', {
              name: summary.stoppedAt,
              items: summary.itemsCreated,
              categories: summary.categoriesCreated,
            })
          : t('starterMenus.done', {
              categories: summary.categoriesCreated,
              items: summary.itemsCreated,
              groups: summary.groupsCreated,
            })
      );
    } catch (error) {
      console.error('Error applying starter menu:', error);
      showToast(t('starterMenus.error'));
    } finally {
      setStarterProgress(null);
    }
  };

  // Toast recibido por navegación (ej: el wizard de combos NER-4488 vuelve acá
  // tras publicar). Se consume una vez y se limpia el state para no repetirlo.
  const location = useLocation();
  useEffect(() => {
    if (location.state?.toast) {
      showToast(location.state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleToggleAvailability = async (itemId) => {
    // Find item to know its current state for the success message
    let wasAvailable = false;
    for (const cat of menu) {
      const item = (cat.items || []).find((i) => i.itemId === itemId);
      if (item) {
        wasAvailable = item.isAvailable;
        break;
      }
    }

    try {
      await toggleItemAvailability(itemId);
      loadMenu();
      const msgKey = wasAvailable ? 'menu.markedAsUnavailable' : 'menu.markedAsAvailable';
      showToast(t(msgKey), async () => {
        try {
          await toggleItemAvailability(itemId);
          loadMenu();
        } catch {
          alert(t('menu.toggleAvailabilityError'));
        }
      });
    } catch (error) {
      alert(t('menu.toggleAvailabilityError'));
    }
  };

  // Editar categoría
  const handleOpenEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryForm({
      name: category.name,
      displayOrder: category.displayOrder || 1,
      imageUrl: category.imageUrl || '',
      translations: category.translations || {},
      timeRange: category.timeRange || null,
      preparationArea: category.preparationArea || 'kitchen',
    });
    setShowEditCategoryModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editCategoryForm.name || !editCategoryForm.name.trim()) {
      setCategoryErrors({ name: t('menu.validation.nameRequired') });
      return;
    }
    setCategoryErrors({});
    try {
      await updateCategory(editingCategory.categoryId, editCategoryForm);
      setShowEditCategoryModal(false);
      setEditingCategory(null);
      loadMenu();
    } catch (error) {
      alert(t('menu.updateCategoryError'));
    }
  };

  // Eliminar categoría
  const handleDeleteCategoryClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteCategoryConfirm(true);
  };

  const handleDeleteCategory = async () => {
    try {
      await deleteCategory(categoryToDelete.categoryId);
      setShowDeleteCategoryConfirm(false);
      setCategoryToDelete(null);
      loadMenu();
    } catch (error) {
      alert(t('menu.deleteCategoryError'));
    }
  };

  // Editar item - navegar a página dedicada
  const handleOpenEditItem = (item, categoryId) => {
    // Combos creados con plantilla (NER-4488) se editan en el wizard; los combos
    // legacy y los productos estándar siguen usando el formulario completo.
    if (item.isCombo && item.comboItems?.templateId) {
      navigate(`/menu/combos/${item.itemId}/edit`);
      return;
    }
    const preparedItem = prepareItemForEdit(item, categoryId);
    navigate(`/menu/items/edit/${item.itemId}`, { state: { item: preparedItem, categoryId } });
  };

  // Eliminar item
  const handleDeleteItemClick = (item) => {
    setItemToDelete(item);
    setShowDeleteItemConfirm(true);
  };

  const handleDeleteItem = async () => {
    try {
      await deleteItem(itemToDelete.itemId);
      setShowDeleteItemConfirm(false);
      setItemToDelete(null);
      loadMenu();
    } catch (error) {
      alert(t('menu.deleteItemError'));
    }
  };

  // Check if filters are active (hide reorder when filtering)
  const isFiltering = searchQuery || filterCategory || filterStatus;

  // Reorder categories
  const handleMoveCategory = async (categoryIndex, direction) => {
    const targetIndex = categoryIndex + direction;
    if (targetIndex < 0 || targetIndex >= menu.length) return;

    const newMenu = [...menu];
    const [moved] = newMenu.splice(categoryIndex, 1);
    newMenu.splice(targetIndex, 0, moved);

    // Update displayOrder for both swapped categories
    const updatedMenu = newMenu.map((cat, idx) => ({ ...cat, displayOrder: idx + 1 }));
    setMenu(updatedMenu);

    try {
      await Promise.all([
        updateCategory(updatedMenu[categoryIndex].categoryId, {
          displayOrder: updatedMenu[categoryIndex].displayOrder,
        }),
        updateCategory(updatedMenu[targetIndex].categoryId, {
          displayOrder: updatedMenu[targetIndex].displayOrder,
        }),
      ]);
    } catch (error) {
      console.error('Error reordering categories:', error);
      loadMenu();
    }
  };

  // Reorder items within a category
  const handleMoveItem = async (categoryId, itemIndex, direction) => {
    const catIdx = menu.findIndex((c) => c.categoryId === categoryId);
    if (catIdx === -1) return;

    const items = [...menu[catIdx].items];
    const targetIndex = itemIndex + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const [moved] = items.splice(itemIndex, 1);
    items.splice(targetIndex, 0, moved);

    const updatedItems = items.map((item, idx) => ({ ...item, displayOrder: idx + 1 }));
    const newMenu = [...menu];
    newMenu[catIdx] = { ...newMenu[catIdx], items: updatedItems };
    setMenu(newMenu);

    try {
      await Promise.all([
        updateItem(updatedItems[itemIndex].itemId, {
          displayOrder: updatedItems[itemIndex].displayOrder,
        }),
        updateItem(updatedItems[targetIndex].itemId, {
          displayOrder: updatedItems[targetIndex].displayOrder,
        }),
      ]);
    } catch (error) {
      console.error('Error reordering items:', error);
      loadMenu();
    }
  };

  // Format availability info for display
  const formatAvailability = (availability) => {
    if (!availability || (!availability.days?.length && !availability.timeSlots?.length)) {
      return 'Siempre disponible';
    }

    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const parts = [];

    // Format days
    if (availability.days && availability.days.length > 0 && availability.days.length < 7) {
      const sortedDays = [...availability.days].sort((a, b) => a - b);
      parts.push(sortedDays.map((d) => dayNames[d]).join(', '));
    }

    // Format time slots
    if (availability.timeSlots && availability.timeSlots.length > 0) {
      const timeRanges = availability.timeSlots
        .map((slot) => `${slot.start}-${slot.end}`)
        .join(', ');
      parts.push(timeRanges);
    } else if (availability.days && availability.days.length > 0) {
      parts.push('Todo el día');
    }

    return parts.length > 0 ? parts.join(' • ') : 'Siempre disponible';
  };

  if (loading) {
    return (
      <div className="menu-page">
        <MenuNav />
        <div className="page-header">
          <div
            className="skeleton-line skeleton-pulse"
            style={{ width: '120px', height: '28px' }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <div
              className="skeleton-line skeleton-pulse"
              style={{ width: '140px', height: '36px', borderRadius: 'var(--radius-md)' }}
            />
            <div
              className="skeleton-line skeleton-pulse"
              style={{ width: '120px', height: '36px', borderRadius: 'var(--radius-md)' }}
            />
          </div>
        </div>
        <div className="menu-filters">
          <div
            className="skeleton-line skeleton-pulse"
            style={{ flex: 1, minWidth: '200px', height: '38px', borderRadius: 'var(--radius-md)' }}
          />
          <div
            className="skeleton-line skeleton-pulse"
            style={{ width: '160px', height: '38px', borderRadius: 'var(--radius-md)' }}
          />
          <div
            className="skeleton-line skeleton-pulse"
            style={{ width: '160px', height: '38px', borderRadius: 'var(--radius-md)' }}
          />
        </div>
        <div className="menu-list">
          {[0, 1].map((catIdx) => (
            <div key={catIdx} className="card category-card">
              <div className="category-header">
                <div
                  className="skeleton-line skeleton-pulse"
                  style={{ width: '180px', height: '24px' }}
                />
              </div>
              <div className="items-list">
                {[0, 1, 2].map((itemIdx) => (
                  <div key={itemIdx} className="menu-item">
                    <div
                      className="skeleton-line skeleton-pulse"
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: 'var(--radius-md)',
                        flexShrink: 0,
                      }}
                    />
                    <div className="item-info">
                      <div
                        className="skeleton-line skeleton-pulse"
                        style={{ width: '60%', height: '18px', marginBottom: '8px' }}
                      />
                      <div
                        className="skeleton-line skeleton-pulse"
                        style={{ width: '90%', height: '14px', marginBottom: '8px' }}
                      />
                      <div
                        className="skeleton-line skeleton-pulse"
                        style={{ width: '80px', height: '20px' }}
                      />
                    </div>
                    <div
                      className="skeleton-line skeleton-pulse"
                      style={{
                        width: '70px',
                        height: '26px',
                        borderRadius: 'var(--radius-sm)',
                        flexShrink: 0,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="menu-page">
      <MenuNav />
      <div className="page-header">
        <h1>{t('menu.title')}</h1>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
          >
            + {t('menu.createCategory')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/menu/items/new')}
            style={{ marginLeft: '10px' }}
          >
            + {t('menu.createItem')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/menu/combos/new')}
            style={{ marginLeft: '10px' }}
          >
            + {t('menu.createCombo')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowStarter((prev) => !prev)}
            style={{ marginLeft: '10px' }}
            aria-expanded={showStarter || menu.length === 0}
          >
            <Sparkles size={16} aria-hidden="true" /> {t('starterMenus.button')}
          </button>
        </div>
      </div>

      {starterProgress?.running && (
        <div className="starter-progress" role="status" aria-live="polite">
          <progress value={starterProgress.done} max={starterProgress.total || 1} />
          <span>
            {t('starterMenus.progress', {
              done: starterProgress.done,
              total: starterProgress.total,
              label: starterProgress.label,
            })}
          </span>
        </div>
      )}

      {!starterProgress?.running && (showStarter || menu.length === 0) && (
        <TemplatePicker
          title={t('starterMenus.title')}
          hint={menu.length === 0 ? t('starterMenus.emptyHint') : t('starterMenus.hint')}
          helpTopicKey="menu.starterMenu"
          templates={STARTER_MENUS.map((starter) => ({
            id: starter.id,
            icon: <StarterIcon name={starter.icon} />,
            name: t(`starterMenus.${starter.id}.name`),
            description: t(`starterMenus.${starter.id}.description`),
            chips: [
              t('starterMenus.chips.categories', { count: starter.categories.length }),
              t('starterMenus.chips.items', { count: countStarterItems(starter) }),
              ...(starter.modifierTemplateIds.length
                ? [t('starterMenus.chips.groups', { count: starter.modifierTemplateIds.length })]
                : []),
            ],
          }))}
          onSelect={handleApplyStarter}
          onClose={menu.length === 0 ? undefined : () => setShowStarter(false)}
        />
      )}

      <div className="menu-filters">
        <input
          type="text"
          className="menu-filters__search"
          placeholder={t('menu.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="menu-filters__select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          aria-label={t('menu.filterByCategory')}
        >
          <option value="">{t('menu.allCategories')}</option>
          {menu.map((cat) => (
            <option key={cat.categoryId} value={cat.categoryId}>
              {cat.name}
            </option>
          ))}
        </select>
        <select
          className="menu-filters__select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label={t('menu.filterByStatus')}
        >
          <option value="">{t('menu.allStatuses')}</option>
          <option value="available">{t('menu.available')}</option>
          <option value="unavailable">{t('menu.unavailable')}</option>
        </select>
      </div>

      {showNewCategoryForm && (
        <div className="card new-category-form" ref={newCategoryFormRef}>
          <h2>{t('menu.newCategory')}</h2>
          <form onSubmit={handleCreateCategory}>
            <p className="required-legend">
              <span className="required">*</span> {t('common.requiredFieldsLegend')}
            </p>
            <TranslatableInput
              label={t('menu.name')}
              labelKey="menu.name"
              type="text"
              value={newCategory.name}
              onChange={(value) => {
                setNewCategory({ ...newCategory, name: value });
                if (categoryErrors.name) setCategoryErrors({});
              }}
              translations={newCategory.translations}
              onTranslationsChange={(translations) =>
                setNewCategory({ ...newCategory, translations })
              }
              required
              error={categoryErrors.name}
            />
            <ImageUpload
              resourceType="categories"
              resourceId={`temp-${Date.now()}`}
              currentImageUrl={newCategory.imageUrl}
              onImageUploaded={(url) => setNewCategory({ ...newCategory, imageUrl: url })}
            />
            <div className="form-group">
              <label>{t('menu.preparationArea')}</label>
              <select
                value={newCategory.preparationArea}
                onChange={(e) =>
                  setNewCategory({ ...newCategory, preparationArea: e.target.value })
                }
              >
                <option value="kitchen">{t('menu.preparationAreaKitchen')}</option>
                <option value="bar">{t('menu.preparationAreaBar')}</option>
                <option value="both">{t('menu.preparationAreaBoth')}</option>
              </select>
            </div>
            <details className="time-range-section">
              <summary className="time-range-summary">
                <Clock size={16} />
                {t('menu.timeAvailability')}
                <span className="optional-label">({t('common.optional')})</span>
              </summary>
              <div className="time-range-fields">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!newCategory.timeRange}
                    onChange={(e) => {
                      setNewCategory({
                        ...newCategory,
                        timeRange: e.target.checked ? { start: '07:00', end: '11:00' } : null,
                      });
                    }}
                  />
                  {t('menu.prioritizeInTimeRange')}
                </label>
                {newCategory.timeRange && (
                  <div className="time-range-inputs">
                    <div className="form-group">
                      <label>{t('common.from')}</label>
                      <input
                        type="time"
                        value={newCategory.timeRange.start}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            timeRange: { ...newCategory.timeRange, start: e.target.value },
                          })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('common.to')}</label>
                      <input
                        type="time"
                        value={newCategory.timeRange.end}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            timeRange: { ...newCategory.timeRange, end: e.target.value },
                          })
                        }
                      />
                    </div>
                    <p className="time-range-hint">
                      {t('menu.timeRangeHint', {
                        start: newCategory.timeRange.start,
                        end: newCategory.timeRange.end,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </details>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowNewCategoryForm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {t('menu.createCategory')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="menu-list">
        {filteredMenu.length === 0 && (searchQuery || filterCategory || filterStatus) && (
          <p className="empty-category">{t('menu.noResults')}</p>
        )}
        {filteredMenu.map((category, catIndex) => (
          <div key={category.categoryId} className="card category-card">
            <div className="category-header">
              <div className="category-header__left">
                {!isFiltering && (
                  <div className="reorder-arrows">
                    <button
                      type="button"
                      className="reorder-arrow"
                      onClick={() => handleMoveCategory(catIndex, -1)}
                      disabled={catIndex === 0}
                      title={t('menu.moveUp')}
                      aria-label={t('menu.moveUp')}
                    >
                      <ChevronUp size={18} />
                    </button>
                    <button
                      type="button"
                      className="reorder-arrow"
                      onClick={() => handleMoveCategory(catIndex, 1)}
                      disabled={catIndex === filteredMenu.length - 1}
                      title={t('menu.moveDown')}
                      aria-label={t('menu.moveDown')}
                    >
                      <ChevronDown size={18} />
                    </button>
                  </div>
                )}
                <h2>{category.name}</h2>
                {category.timeRange && (
                  <span
                    className="time-range-indicator"
                    title={t('menu.prioritizedFromTo', {
                      start: category.timeRange.start,
                      end: category.timeRange.end,
                    })}
                  >
                    <Clock size={14} />
                    <span>
                      {category.timeRange.start} – {category.timeRange.end}
                    </span>
                  </span>
                )}
              </div>
              <ActionMenu
                actions={[
                  {
                    label: t('common.edit'),
                    icon: '✏️',
                    onClick: () => handleOpenEditCategory(category),
                    variant: 'default',
                  },
                  {
                    label: t('common.delete'),
                    icon: '🗑️',
                    onClick: () => handleDeleteCategoryClick(category),
                    variant: 'danger',
                  },
                ]}
              />
            </div>
            <div className="items-list">
              {category.items && category.items.length > 0 ? (
                category.items.map((item, itemIndex) => (
                  <div
                    key={item.itemId}
                    className="menu-item menu-item--clickable"
                    onClick={() => handleOpenEditItem(item, category.categoryId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenEditItem(item, category.categoryId);
                      }
                    }}
                  >
                    {!isFiltering && category.items.length > 1 && (
                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
                      <div
                        className="reorder-arrows reorder-arrows--item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="reorder-arrow"
                          onClick={() => handleMoveItem(category.categoryId, itemIndex, -1)}
                          disabled={itemIndex === 0}
                          title={t('menu.moveUp')}
                          aria-label={t('menu.moveUp')}
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          type="button"
                          className="reorder-arrow"
                          onClick={() => handleMoveItem(category.categoryId, itemIndex, 1)}
                          disabled={itemIndex === category.items.length - 1}
                          title={t('menu.moveDown')}
                          aria-label={t('menu.moveDown')}
                        >
                          <ChevronDown size={16} />
                        </button>
                      </div>
                    )}
                    {item.imageUrl ? (
                      <LazyImage
                        src={item.imageUrl}
                        alt={item.name}
                        className="item-image menu-item-image"
                        thumbnailSize={THUMBNAIL_SIZES.SMALL}
                      />
                    ) : (
                      <div className="item-image-placeholder">
                        <Camera size={24} />
                        <span>{t('menu.noImage')}</span>
                      </div>
                    )}
                    <div className="item-info">
                      <h2>{item.name}</h2>
                      <p>{item.description}</p>
                      <strong>{formatCurrency(item.price)}</strong>
                      {item.isPromotion && (
                        <span className="item-badge item-badge--promo">PROMO</span>
                      )}
                      {item.isDailySpecial && (
                        <span className="item-badge item-badge--special">Del día</span>
                      )}
                      {item.isFeatured && (
                        <span className="item-badge item-badge--featured">Destacado</span>
                      )}
                      {item.availability && (
                        <div className="availability-info">
                          <span className="availability-icon">🕒</span>
                          <span className="availability-text">
                            {formatAvailability(item.availability)}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                      <span
                        className={`status-badge ${item.isAvailable ? 'available' : 'unavailable'}`}
                      >
                        <span className="status-badge-dot"></span>
                        {item.isAvailable ? t('menu.available') : t('menu.unavailable')}
                      </span>
                      <ActionMenu
                        actions={[
                          {
                            label: item.isAvailable
                              ? t('menu.markAsUnavailable')
                              : t('menu.markAsAvailable'),
                            icon: item.isAvailable ? '⭕' : '✅',
                            onClick: () => handleToggleAvailability(item.itemId),
                            variant: item.isAvailable ? 'warning' : 'success',
                          },
                          {
                            label: t('common.edit'),
                            icon: '✏️',
                            onClick: () => handleOpenEditItem(item, category.categoryId),
                            variant: 'default',
                          },
                          {
                            label: t('common.delete'),
                            icon: '🗑️',
                            onClick: () => handleDeleteItemClick(item),
                            variant: 'danger',
                          },
                        ]}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-category">{t('menu.emptyCategory')}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal Editar Categoría */}
      {showEditCategoryModal && editingCategory && (
        <div className="modal-overlay" onClick={() => setShowEditCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('menu.editCategory')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowEditCategoryModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateCategory}>
              <div className="modal-body">
                <p className="required-legend">
                  <span className="required">*</span> {t('common.requiredFieldsLegend')}
                </p>
                <TranslatableInput
                  label={t('menu.name')}
                  labelKey="menu.name"
                  type="text"
                  value={editCategoryForm.name}
                  onChange={(value) => {
                    setEditCategoryForm({ ...editCategoryForm, name: value });
                    if (categoryErrors.name) setCategoryErrors({});
                  }}
                  translations={editCategoryForm.translations}
                  onTranslationsChange={(translations) =>
                    setEditCategoryForm({ ...editCategoryForm, translations })
                  }
                  required
                  error={categoryErrors.name}
                />
                <ImageUpload
                  resourceType="categories"
                  resourceId={editingCategory.categoryId}
                  currentImageUrl={editCategoryForm.imageUrl}
                  onImageUploaded={(url) =>
                    setEditCategoryForm({ ...editCategoryForm, imageUrl: url })
                  }
                />
                <div className="form-group">
                  <label>{t('menu.preparationArea')}</label>
                  <select
                    value={editCategoryForm.preparationArea}
                    onChange={(e) =>
                      setEditCategoryForm({
                        ...editCategoryForm,
                        preparationArea: e.target.value,
                      })
                    }
                  >
                    <option value="kitchen">{t('menu.preparationAreaKitchen')}</option>
                    <option value="bar">{t('menu.preparationAreaBar')}</option>
                    <option value="both">{t('menu.preparationAreaBoth')}</option>
                  </select>
                </div>
                <details className="time-range-section" open={!!editCategoryForm.timeRange}>
                  <summary className="time-range-summary">
                    <Clock size={16} />
                    {t('menu.timeAvailability')}
                    <span className="optional-label">({t('common.optional')})</span>
                  </summary>
                  <div className="time-range-fields">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!editCategoryForm.timeRange}
                        onChange={(e) => {
                          setEditCategoryForm({
                            ...editCategoryForm,
                            timeRange: e.target.checked ? { start: '07:00', end: '11:00' } : null,
                          });
                        }}
                      />
                      {t('menu.prioritizeInTimeRange')}
                    </label>
                    {editCategoryForm.timeRange && (
                      <div className="time-range-inputs">
                        <div className="form-group">
                          <label>{t('common.from')}</label>
                          <input
                            type="time"
                            value={editCategoryForm.timeRange.start}
                            onChange={(e) =>
                              setEditCategoryForm({
                                ...editCategoryForm,
                                timeRange: {
                                  ...editCategoryForm.timeRange,
                                  start: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('common.to')}</label>
                          <input
                            type="time"
                            value={editCategoryForm.timeRange.end}
                            onChange={(e) =>
                              setEditCategoryForm({
                                ...editCategoryForm,
                                timeRange: {
                                  ...editCategoryForm.timeRange,
                                  end: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                        <p className="time-range-hint">
                          {t('menu.timeRangeHint', {
                            start: editCategoryForm.timeRange.start,
                            end: editCategoryForm.timeRange.end,
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </details>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowEditCategoryModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminar Categoría */}
      {showDeleteCategoryConfirm && categoryToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteCategoryConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('menu.deleteCategory')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteCategoryConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('menu.deleteCategoryConfirmation', { name: categoryToDelete.name })}</p>
              <p className="warning-text">{t('menu.deleteCategoryWarning')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteCategoryConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteCategory}>
                {t('menu.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminar Item */}
      {/* Toast de feedback */}
      {toast && (
        <div className="menu-toast menu-toast--success" role="status" aria-live="polite">
          <span>{toast.message}</span>
          {toast.undoCallback && (
            <button
              type="button"
              className="menu-toast__undo"
              onClick={() => {
                toast.undoCallback();
                setToast(null);
                if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
              }}
            >
              {t('common.undo')}
            </button>
          )}
        </div>
      )}

      {starterConfirmId && (
        <div className="modal-overlay" onClick={() => setStarterConfirmId(null)}>
          <div
            className="modal-content modal-small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="starter-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="starter-confirm-title">{t('starterMenus.confirmTitle')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setStarterConfirmId(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('starterMenus.confirmNotEmpty')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setStarterConfirmId(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleApplyStarter(starterConfirmId, { confirmed: true })}
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteItemConfirm && itemToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteItemConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('menu.deleteItem')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteItemConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('menu.deleteItemConfirmation', { name: itemToDelete.name })}</p>
              <p className="warning-text">{t('menu.deleteWarning')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteItemConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteItem}>
                {t('menu.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuPage;
